import { useMemo } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  RAILS_BY_RUNTIME_CORRIDOR,
  RUNTIME_CORRIDOR_ISO3,
} from "@/modules/dashboard/dispersions/constants/dispersions.constants";
import type { PayoutRail } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { useRuntimeWallets } from "@/modules/dashboard/wallets/hooks/useRuntimeWallets";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";

import { corridorValue } from "../utils/batchSetupSchema";

export interface CorridorOption {
  /** `ISO3:RAIL`, the form value. */
  value: string;
  country: string;
  rail: PayoutRail;
  /** Delivery currency of the corridor. */
  currency: string;
  /** False when the corridor has no route or its rail is switched off; the catalog fails open. */
  available: boolean;
  /** Its rail is switched off: shown as maintenance rather than as missing. */
  inMaintenance: boolean;
}

export interface UseBatchSetupOptionsResult {
  wallets: RuntimeWallet[];
  walletsLoading: boolean;
  walletsError: string | null;
  walletsHasAccess: boolean;
  corridorOptions: CorridorOption[];
  corridorsLoading: boolean;
  /** False for staff until they pick the organization the batch belongs to. */
  canLoad: boolean;
}

/**
 * The two catalogs the setup step chooses from: the organization's activated wallets (the debit side) and the
 * payout corridors (the delivery side), each filtered by the runtime's availability. Members are pinned
 * server-side; staff pass the organization they act for, and nothing loads until they have.
 */
export function useBatchSetupOptions(organizationId: string | null): UseBatchSetupOptionsResult {
  const { isInternalUser } = useAuth();
  const scopedOrganization = isInternalUser ? (organizationId ?? undefined) : undefined;
  const wallets = useRuntimeWallets(scopedOrganization);
  const corridors = useAvailableCorridors(scopedOrganization);

  const corridorOptions = useMemo<CorridorOption[]>(
    () =>
      RUNTIME_CORRIDOR_ISO3.flatMap((country) =>
        RAILS_BY_RUNTIME_CORRIDOR[country].map((rail) => {
          const currency = CURRENCY_BY_RUNTIME_CORRIDOR[country];
          return {
            value: corridorValue(country, rail),
            country,
            rail,
            currency,
            available: corridors.isAvailable(rail, country, currency, "PAYOUT"),
            inMaintenance: corridors.inMaintenance({ rail, country, currency, direction: "PAYOUT" }),
          };
        }),
      ),
    [corridors],
  );

  return {
    wallets: wallets.wallets,
    walletsLoading: wallets.isLoading,
    walletsError: wallets.error,
    walletsHasAccess: wallets.hasAccess,
    corridorOptions,
    corridorsLoading: corridors.isLoading,
    canLoad: !isInternalUser || organizationId !== null,
  };
}
